import { WebsiteWithConnections } from '../../types/website';
import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Card, Input } from '../ui';
import { InfoField, EmptyValue, WebsiteEditForm } from './WebsiteInfoPanel';

interface FrontendInfoPanelProps {
  website: WebsiteWithConnections | null;
  isEditMode: boolean;
  editForm: WebsiteEditForm;
  setEditForm: (val: WebsiteEditForm) => void;
}

export default function FrontendInfoPanel({ website, isEditMode, editForm, setEditForm }: FrontendInfoPanelProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [decryptedPassword, setDecryptedPassword] = useState('••••••••');

  // Function to toggle password visibility
  const togglePasswordVisibility = async () => {
    if (!showPassword && website?.frontend_password) {
      if (!String(website.frontend_password).startsWith('\\x')) {
        setDecryptedPassword(website.frontend_password);
      } else {
        try {
          const { data, error } = await supabase.rpc(
            'decrypt_frontend_password',
            { encrypted_password: website.frontend_password }
          );
          if (!error && data) setDecryptedPassword(data);
        } catch (err) {
          console.error('Error decrypting password:', err);
          setDecryptedPassword('Error decrypting');
        }
      }
    }
    setShowPassword(!showPassword);
  };

  return (
    <Card eyebrow="Frontend">
      {isEditMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <Input
              label="Platform"
              placeholder="React, Vue, WordPress, etc."
              value={editForm.frontend_platform}
              onChange={(e) => setEditForm({ ...editForm, frontend_platform: e.target.value })}
            />
            <Input
              label="Theme"
              placeholder="Theme name or custom"
              value={editForm.frontend_theme}
              onChange={(e) => setEditForm({ ...editForm, frontend_theme: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <Input
              label="Login Username"
              placeholder="admin, editor, etc."
              value={editForm.frontend_username || ''}
              onChange={(e) => setEditForm({ ...editForm, frontend_username: e.target.value })}
            />
            <Input
              label="Login Password"
              type="password"
              placeholder="Password"
              value={editForm.frontend_password || ''}
              onChange={(e) => setEditForm({ ...editForm, frontend_password: e.target.value })}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-5) var(--space-6)' }}>
          <InfoField label="Platform">
            {website?.frontend_platform || <EmptyValue />}
          </InfoField>

          <InfoField label="Theme">
            {website?.frontend_theme || <EmptyValue />}
          </InfoField>

          <InfoField label="Login Username" mono>
            {website?.frontend_username || <EmptyValue />}
          </InfoField>

          <InfoField label="Login Password" mono>
            {website?.frontend_password ? (
              <>
                <span>{showPassword ? decryptedPassword : '••••••••'}</span>
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="pressable hover-wash"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px',
                    borderRadius: 'var(--radius-xs)',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-faint)',
                    cursor: 'pointer',
                  }}
                  aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </>
            ) : (
              <EmptyValue />
            )}
          </InfoField>
        </div>
      )}
    </Card>
  );
}
